import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Space } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { errorText, saveBlob } from './format';

const ACCOUNT_RULE = { pattern: /^\d{3,10}$/ };
// Same as the server's AB_PARTNER_CODE.
const PARTNER_RULE = /^[A-Za-z0-9ČĆŽŠĐčćžšđ._/-]{1,20}$/;

/**
 * Journal file for AB Soft booking the selected PDPO returns: withholding tax
 * expense and late-payment interest against the liability for the tax rate,
 * all with the return's BOP. The accounts differ per company, so they are
 * editable here and the ones used are offered again next time. Each recipient
 * gets its AB partner code (found by name, or typed once and remembered), so
 * the lines show whose tax it is.
 */
export default function AbJournalModal({ open, company, returns, onClose, onExported }) {
  const { t } = useTranslation('taxReturns');
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const queryClient = useQueryClient();

  const ids = returns.map((r) => r._id);
  const { data: settings } = useQuery({
    queryKey: ['tax-returns-ab-settings', company, ids],
    queryFn: () => API.taxReturns.getAbSettings(company, ids),
    enabled: open && Boolean(company),
  });
  // `preserve`: the list is drawn only once it has rows, so it is watched before its fields exist.
  const partners = Form.useWatch('partners', { form, preserve: true }) || [];
  const withoutCode = partners.filter((p) => !p?.code?.trim()).length;

  useEffect(() => {
    if (!open || !settings) return;
    const pdpo = settings.pdpo || {};
    // Accountants book a month's returns on its last day.
    const [latest] = returns
      .map((r) => r.incomeDate || r.dueDate)
      .filter(Boolean)
      .map((d) => dayjs(d))
      .sort((a, b) => b.valueOf() - a.valueOf());
    form.setFieldsValue({
      folder: pdpo.folder || '',
      date: (latest || dayjs()).endOf('month'),
      taxExpense: pdpo.taxExpense,
      interestExpense: pdpo.interestExpense,
      liabilities: Object.entries(pdpo.liabilityByRate || {})
        .map(([rate, account]) => ({ rate: Number(rate), account }))
        .sort((a, b) => b.rate - a.rate),
      partners: (settings.recipients || []).map((r) => ({ name: r.name, code: r.code || '' })),
    });
  }, [open, settings, returns, form]);

  const mutation = useMutation({
    mutationFn: (v) =>
      API.taxReturns.exportAbJournal({
        company,
        ids,
        folder: v.folder.trim(),
        date: v.date.format('YYYY-MM-DD'),
        accounts: {
          taxExpense: v.taxExpense,
          interestExpense: v.interestExpense,
          liabilityByRate: Object.fromEntries((v.liabilities || []).map((l) => [l.rate, l.account])),
        },
        partners: Object.fromEntries((v.partners || []).map((p) => [p.name, (p.code || '').trim()])),
      }),
    onSuccess: ({ blob, count, warnings }) => {
      saveBlob(blob, 'NALOG.xls');
      setResult({ count, warnings });
      // Codes typed here are remembered on the server; the next opening should show them.
      queryClient.invalidateQueries({ queryKey: ['tax-returns-ab-settings', company] });
      onExported?.();
    },
  });

  const close = () => {
    setResult(null);
    mutation.reset();
    onClose();
  };

  const already = returns.filter((r) => r.abExport);

  return (
    <Modal
      open={open}
      // After the export the page clears its selection, so the title keeps the exported count.
      title={t('ab.title', { count: result ? result.count : returns.length })}
      width={640}
      onCancel={close}
      okText={result ? t('import.close') : t('ab.submit')}
      cancelButtonProps={{ shape: 'round', style: { display: result ? 'none' : undefined } }}
      okButtonProps={{ shape: 'round', loading: mutation.isPending }}
      onOk={result ? close : () => form.submit()}
      destroyOnClose
    >
      {result ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="success" showIcon message={t('ab.done', { count: result.count })} description={t('ab.howToImport')} />
          {result.warnings.length > 0 && <Alert type="warning" showIcon message={result.warnings.join('; ')} />}
        </Space>
      ) : (
        <Form form={form} layout="vertical" onFinish={(v) => mutation.mutate(v)}>
          <p className="mb-3 text-sm opacity-70">{t('ab.help')}</p>
          {already.length > 0 && (
            <Alert
              style={{ marginBottom: 12 }}
              type="warning"
              showIcon
              message={t('ab.alreadyExported', { count: already.length })}
            />
          )}
          <Space wrap align="start">
            <Form.Item name="folder" label={t('ab.folder')} rules={[{ required: true, whitespace: true }]}>
              <Input style={{ width: 120 }} maxLength={10} />
            </Form.Item>
            <Form.Item name="date" label={t('ab.date')} rules={[{ required: true }]}>
              <DatePicker format="DD.MM.YYYY" />
            </Form.Item>
          </Space>
          <Space wrap align="start">
            <Form.Item
              name="taxExpense"
              label={t('ab.taxExpense')}
              rules={[{ required: true }, { ...ACCOUNT_RULE, message: t('errors.ab_invalid_account') }]}
            >
              <Input style={{ width: 160 }} />
            </Form.Item>
            <Form.Item
              name="interestExpense"
              label={t('ab.interestExpense')}
              rules={[{ required: true }, { ...ACCOUNT_RULE, message: t('errors.ab_invalid_account') }]}
            >
              <Input style={{ width: 160 }} />
            </Form.Item>
          </Space>
          <div className="mb-2 text-sm">{t('ab.liabilities')}</div>
          <Form.List name="liabilities">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} align="start" style={{ display: 'flex' }}>
                    <Form.Item name={[field.name, 'rate']} rules={[{ required: true }]}>
                      <InputNumber min={0} max={100} addonAfter="%" style={{ width: 110 }} />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'account']}
                      rules={[{ required: true }, { ...ACCOUNT_RULE, message: t('errors.ab_invalid_account') }]}
                    >
                      <Input style={{ width: 160 }} />
                    </Form.Item>
                    <Button shape="round" icon={<DeleteOutlined />} onClick={() => remove(field.name)} aria-label={t('ab.removeRate')} />
                  </Space>
                ))}
                <Button shape="round" icon={<PlusOutlined />} onClick={() => add({ rate: null, account: '' })}>
                  {t('ab.addRate')}
                </Button>
              </>
            )}
          </Form.List>
          {partners.length > 0 && (
            <>
              <div className="mb-1 mt-4 text-sm">{t('ab.partners')}</div>
              <p className="mb-2 text-xs opacity-70">{t('ab.partnersHelp')}</p>
              <Form.List name="partners">
                {(fields) =>
                  fields.map((field) => (
                    <Space key={field.key} align="start" style={{ display: 'flex' }}>
                      <Form.Item name={[field.name, 'name']} hidden>
                        <Input />
                      </Form.Item>
                      <div style={{ width: 300, paddingTop: 5 }} className="truncate text-sm" title={partners[field.name]?.name}>
                        {partners[field.name]?.name}
                      </div>
                      <Form.Item
                        name={[field.name, 'code']}
                        rules={[{ pattern: PARTNER_RULE, message: t('errors.ab_invalid_partner') }]}
                      >
                        <Input style={{ width: 160 }} maxLength={20} placeholder={t('ab.partnerPlaceholder')} />
                      </Form.Item>
                    </Space>
                  ))
                }
              </Form.List>
              {withoutCode > 0 && (
                <Alert type="warning" showIcon message={t('ab.partnerMissing', { count: withoutCode })} />
              )}
            </>
          )}
          {mutation.isError && (
            <Alert style={{ marginTop: 12 }} type="error" showIcon message={errorText(t, mutation.error)} />
          )}
        </Form>
      )}
    </Modal>
  );
}
